import type { WorkflowInput } from "@/stores/workflowsStore";

type JsonSchemaLike = Record<string, unknown>;
type InputSchemaDefinitionLike = {
  inputSchema?: WorkflowInput[];
  nodes?: Record<string, { type?: string; payload?: Record<string, unknown> }>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isWorkflowInputType(value: unknown): value is WorkflowInput["type"] {
  return value === "string" || value === "number" || value === "boolean" || value === "object" || value === "array";
}

export function workflowInputsToJsonSchema(inputs: WorkflowInput[]): JsonSchemaLike | undefined {
  const properties: Record<string, JsonSchemaLike> = {};
  const required: string[] = [];

  for (const input of inputs) {
    if (!input.key?.trim() || !isWorkflowInputType(input.type)) continue;

    properties[input.key] = {
      type: input.type,
      ...(input.description ? { description: input.description } : {}),
    };

    if (input.required) {
      required.push(input.key);
    }
  }

  if (Object.keys(properties).length === 0) {
    return undefined;
  }

  return {
    type: "object",
    properties,
    ...(required.length > 0 ? { required } : {}),
  };
}

export function jsonSchemaToWorkflowInputs(schema: unknown): WorkflowInput[] {
  if (!isRecord(schema) || !isRecord(schema.properties)) {
    return [];
  }

  const required = Array.isArray(schema.required) ? schema.required.filter(v => typeof v === "string") : [];
  const inputs: WorkflowInput[] = [];

  for (const [key, value] of Object.entries(schema.properties)) {
    if (!isRecord(value)) continue;

    const type = value.type;
    if (!isWorkflowInputType(type)) continue;

    inputs.push({
      key,
      type,
      description: typeof value.description === "string" && value.description.trim() ? value.description : undefined,
      required: required.includes(key),
    });
  }

  return inputs;
}

export function getEditorInputSchemaFromDefinition(definition?: InputSchemaDefinitionLike | null): WorkflowInput[] {
  if (!definition) return [];

  if (definition.inputSchema?.length) {
    return definition.inputSchema;
  }

  const rootNode = definition.nodes?.root;
  if (!rootNode || rootNode.type !== "manual") {
    return [];
  }

  return jsonSchemaToWorkflowInputs(rootNode.payload?.inputSchema);
}

export function isJsonSchemaBuilderCompatible(schema: unknown): boolean {
  if (!isRecord(schema)) return true;
  if (schema.type !== undefined && schema.type !== "object") return false;
  if (schema.additionalProperties !== undefined && schema.additionalProperties !== false) return false;
  if (!schema.properties) return true;
  if (!isRecord(schema.properties)) return false;

  return Object.values(schema.properties).every(value => {
    if (!isRecord(value) || !isWorkflowInputType(value.type)) {
      return false;
    }

    const extraKeys = Object.keys(value).filter(key => !["type", "description", "title"].includes(key));
    return extraKeys.length === 0;
  });
}
